import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail, Phone, MapPin, User, Edit, KeyRound, ChevronDown, Heart, Link2, Unlink, HelpCircle, LogOut } from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { provinceName, countryName } from "@shared/constants/provinces";
import { FlagIcon } from "@/components/FlagIcon";
import type { Client } from "@shared/schema";

interface ClientHeaderProps {
  client: Client;
  onEdit?: () => void;
  onChangePassword?: () => void;
  /** When provided, the popover footer shows account actions (Support, Change
   *  Password, Logout) — i.e. this is the user's OWN dashboard, so the single
   *  consolidated top-bar dropdown replaces the separate UserMenu. */
  onLogout?: () => void;
  readOnly?: boolean;
  compact?: boolean;
}

export function ClientHeader({
  client,
  onEdit,
  onChangePassword,
  onLogout,
  readOnly = false,
  compact = false,
}: ClientHeaderProps) {
  const [linkSpouseOpen, setLinkSpouseOpen] = useState(false);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [selectedSpouseId, setSelectedSpouseId] = useState<string>("");
  const { toast } = useToast();
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Jurisdiction (Stephanie #9) — top-bar badge uses the province CODE ("🇨🇦 ON");
  // the popover location line uses the full name ("🇨🇦 Ontario"). Both read the
  // jurisdiction_* columns, NOT the legacy address country/state. The flag is a
  // Twemoji SVG (FlagIcon), not an emoji, so it renders on Windows too.
  const jurLocationLabel = provinceName(client.jurisdictionProvince) || countryName(client.jurisdictionCountry);

  const { data: spouseClient } = useQuery<Client>({
    queryKey: ["/api/clients", client.spouseClientId],
    enabled: !!client.spouseClientId,
  });

  const { data: availableSpouses = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients", client.id, "available-spouses"],
    enabled: linkSpouseOpen,
  });

  const linkSpouseMutation = useMutation({
    mutationFn: async (spouseClientId: string) => {
      const res = await apiRequest("POST", `/api/clients/${client.id}/link-spouse`, { spouseClientId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      setLinkSpouseOpen(false);
      setSelectedSpouseId("");
      toast({
        title: "Spouse linked",
        description: "The clients have been linked as spouses.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error linking spouse",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const unlinkSpouseMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/clients/${client.id}/link-spouse`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      setUnlinkConfirmOpen(false);
      toast({
        title: "Spouse unlinked",
        description: "The spouse link has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error unlinking spouse",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Popover>
        <PopoverTrigger asChild>
          <button 
            className={`inline-flex items-center gap-1.5 font-semibold hover-elevate rounded-lg transition-colors ${
              compact 
                ? "text-base px-2 py-1" 
                : "text-2xl px-3 py-1.5 -ml-3"
            }`}
            data-testid="button-client-info-popover"
          >
            <span data-testid="text-client-name">{client.name}</span>
            <span className="font-normal text-muted-foreground inline-flex items-center gap-1" data-testid="text-client-jurisdiction-badge">
              <FlagIcon country={client.jurisdictionCountry} />
              {client.jurisdictionProvince ? <span>{client.jurisdictionProvince}</span> : null}
            </span>
            <ChevronDown className={compact ? "h-4 w-4 text-muted-foreground" : "h-5 w-5 text-muted-foreground"} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Client Information</h4>
              {!readOnly && onEdit && (
                <Button aria-label="Edit client information" 
                  variant="ghost" 
                  size="icon"
                  className="h-8 w-8"
                  onClick={onEdit}
                  data-testid="button-edit-client-info"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium">{client.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <span data-testid="text-client-email">{client.email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                <span data-testid="text-client-phone">{client.phone}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="inline-flex items-center gap-1.5" data-testid="text-client-jurisdiction">
                  <FlagIcon country={client.jurisdictionCountry} /> {jurLocationLabel}
                </span>
              </div>
            </div>

            {/* Spouse section - visible to everyone, but only editable when not readOnly */}
            <div className="pt-3 border-t space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center gap-2">
                  <Heart className="h-4 w-4 text-muted-foreground" />
                  Spouse
                </span>
              </div>
              {client.spouseClientId && spouseClient ? (
                <div className="flex items-center justify-between bg-muted/50 rounded-md px-3 py-2">
                  <span className="text-sm" data-testid="text-spouse-name">
                    {spouseClient.name}
                  </span>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-destructive hover:text-destructive"
                      onClick={() => setUnlinkConfirmOpen(true)}
                      data-testid="button-unlink-spouse"
                    >
                      <Unlink className="h-3.5 w-3.5 mr-1" />
                      Unlink
                    </Button>
                  )}
                </div>
              ) : (
                readOnly ? (
                  <p className="text-sm text-muted-foreground">
                    No spouse linked
                  </p>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => setLinkSpouseOpen(true)}
                    data-testid="button-link-spouse"
                  >
                    <Link2 className="h-4 w-4" />
                    Link Spouse
                  </Button>
                )
              )}
            </div>

            {onLogout ? (
              /* Own dashboard — consolidated account actions (replaces the
                 separate top-right UserMenu). */
              <div className="pt-3 border-t space-y-1">
                <button
                  onClick={() => setLocation("/support")}
                  className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover-elevate text-left"
                  data-testid="menu-item-support"
                >
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  Support
                </button>
                {onChangePassword && (
                  <button
                    onClick={onChangePassword}
                    className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover-elevate text-left"
                    data-testid="menu-item-change-password"
                  >
                    <KeyRound className="h-4 w-4 text-muted-foreground" />
                    Change Password
                  </button>
                )}
                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover-elevate text-left text-destructive"
                  data-testid="menu-item-logout"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            ) : (
              !readOnly && onChangePassword && (
                <div className="pt-2 border-t">
                  <Button
                    variant="outline"
                    onClick={onChangePassword}
                    className="w-full gap-2"
                    size="sm"
                    data-testid="button-change-password"
                  >
                    <KeyRound className="h-4 w-4" />
                    Change Password
                  </Button>
                </div>
              )
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={linkSpouseOpen} onOpenChange={setLinkSpouseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link Spouse</DialogTitle>
            <DialogDescription>
              Select another client to link as {client.name}'s spouse. Joint items will be shared between both estates.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {availableSpouses.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No available clients to link as spouse. Other clients may already have a spouse linked.
              </p>
            ) : (
              <Select value={selectedSpouseId} onValueChange={setSelectedSpouseId}>
                <SelectTrigger data-testid="select-spouse">
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {availableSpouses.map((spouse) => (
                    <SelectItem key={spouse.id} value={spouse.id} data-testid={`option-spouse-${spouse.id}`}>
                      {spouse.name} ({spouse.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkSpouseOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => linkSpouseMutation.mutate(selectedSpouseId)}
              disabled={!selectedSpouseId || linkSpouseMutation.isPending}
              data-testid="button-confirm-link-spouse"
            >
              {linkSpouseMutation.isPending ? "Linking..." : "Link Spouse"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={unlinkConfirmOpen} onOpenChange={setUnlinkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Spouse?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the spouse link between {client.name} and {spouseClient?.name}. 
              Joint items will remain but will no longer be shared between estates.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => unlinkSpouseMutation.mutate()}
              disabled={unlinkSpouseMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-unlink-spouse"
            >
              {unlinkSpouseMutation.isPending ? "Unlinking..." : "Unlink"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
